import { useEffect, useRef, useState } from 'react';
import { boxOf, unionBox } from '../utils/floorSnap';

// Zal planı: sabit 1200×800 lövhə, ekranda faizlə miqyaslanır (backend utils/floorPlan.js).
export const CANVAS_W = 1200;
export const CANVAS_H = 800;

export const parsePoints = (raw) => {
    if (Array.isArray(raw)) return raw;
    try {
        const p = JSON.parse(raw || 'null');
        return Array.isArray(p) && p.length >= 3 ? p : null;
    } catch {
        return null;
    }
};

export const isPlaced = (t) => t.fp_x !== null && t.fp_x !== undefined;

// Rəngli formanın üzərində oxunan yazı rəngi
export const readableText = (hex) => {
    const n = parseInt(String(hex).slice(1), 16);
    const light = 0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255);
    return light > 150 ? '#1f2937' : '#ffffff';
};

// Layer sırası: kiçik fp_z arxada. Bərabərdirsə dekor masaların altında qalır.
const layerRank = (t) => (t.kind === 'decor' ? 0 : 1);
export const sortLayers = (list) => [...list].sort((a, b) => (a.fp_z || 0) - (b.fp_z || 0) || layerRank(a) - layerRank(b));

// Lövhənin ekrandakı miqyası (yazı ölçüsü üçün)
export const useCanvasScale = (ref, dep) => {
    const [scale, setScale] = useState(1);
    useEffect(() => {
        if (!ref.current) return undefined;
        const ro = new ResizeObserver(([e]) => setScale(e.contentRect.width / CANVAS_W));
        ro.observe(ref.current);
        return () => ro.disconnect();
    }, [ref, dep]);
    return scale;
};

// Masanın forması. Ölçü nə olursa olsun xətt qalınlığı eyni qalır.
export const FloorShape = ({ table, fill, stroke, strokeWidth = 2 }) => {
    const points = table.fp_shape === 'polygon' ? parsePoints(table.fp_points) : null;
    const paint = { fill, stroke, strokeWidth, vectorEffect: 'non-scaling-stroke' };
    const radius = table.fp_shape === 'rounded' ? 24 : 6;
    return (
        <svg className="absolute inset-0 h-full w-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none">
            {points ? (
                <polygon points={points.map((p) => p.join(',')).join(' ')} {...paint} strokeLinejoin="round" />
            ) : table.fp_shape === 'circle' ? (
                <ellipse cx="50" cy="50" rx="50" ry="50" {...paint} />
            ) : (
                <rect x="0" y="0" width="100" height="100" rx={(radius / table.fp_w) * 100} ry={(radius / table.fp_h) * 100} {...paint} />
            )}
        </svg>
    );
};

// Masanın adı döndürülmüş masada da düz oxunsun
export const FloorLabel = ({ table, scale, sub, color, size = 15 }) => (
    <div
        className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center overflow-hidden px-1 text-center leading-tight"
        style={{ transform: `rotate(${-table.fp_rot}deg)`, color }}
    >
        <span className="max-w-full truncate font-black" style={{ fontSize: Math.max(10, size * scale) }}>{table.name}</span>
        {sub && <span className="max-w-full truncate font-semibold opacity-80" style={{ fontSize: Math.max(9, 12 * scale) }}>{sub}</span>}
    </div>
);

export const tableBoxStyle = (t) => ({
    left: `${(t.fp_x / CANVAS_W) * 100}%`,
    top: `${(t.fp_y / CANVAS_H) * 100}%`,
    width: `${(t.fp_w / CANVAS_W) * 100}%`,
    height: `${(t.fp_h / CANVAS_H) * 100}%`,
    transform: `rotate(${t.fp_rot}deg)`,
});

const FIT_BELOW = 720; // bundan dar ekranda (telefon) plan yalnız masaların olduğu hissəyə yaxınlaşır
const FIT_PAD = 20;

/** Ofisiant/kassir görünüşü. items: [{ table, fill, stroke, color, sub, disabled, title }], decor: dekor formaları (klik yoxdur) */
export default function FloorPlanView({ items, decor = [], onOpen, maxHeight }) {
    const outerRef = useRef(null);
    const ref = useRef(null);
    const [outerW, setOuterW] = useState(0);
    useEffect(() => {
        if (!outerRef.current) return undefined;
        const ro = new ResizeObserver(([e]) => setOuterW(e.contentRect.width));
        ro.observe(outerRef.current);
        return () => ro.disconnect();
    }, []);

    // Telefonda boş yerlər kəsilir, masalar ekranın ortasında və tam enində görünür
    const shapes = [...decor, ...items.map((it) => it.table)].filter((t) => t.fp_x !== null && t.fp_x !== undefined);
    const fit = outerW > 0 && outerW < FIT_BELOW && shapes.length > 0;
    const area = fit ? (() => {
        const b = unionBox(shapes.map(boxOf));
        const l = Math.max(0, b.l - FIT_PAD);
        const t = Math.max(0, b.t - FIT_PAD);
        return { l, t, w: Math.min(CANVAS_W, b.r + FIT_PAD) - l, h: Math.min(CANVAS_H, b.b + FIT_PAD) - t };
    })() : null;

    const scale = useCanvasScale(ref, fit);
    const stack = sortLayers([
        ...decor.map((d) => ({ kind: 'decor', key: `d${d.id}`, fp_z: d.fp_z })),
        ...items.map(({ table }) => ({ kind: 'table', key: `t${table.id}`, fp_z: table.fp_z })),
    ]);
    const zOf = new Map(stack.map((s, i) => [s.key, i + 1]));

    const content = (
        <>
            {decor.map((d) => (
                <div key={d.id} className="pointer-events-none absolute" style={{ ...tableBoxStyle(d), zIndex: zOf.get(`d${d.id}`) }}>
                    <FloorShape table={d} fill={d.fill} stroke="rgba(0,0,0,.15)" strokeWidth={1} />
                    <FloorLabel table={{ ...d, name: d.label }} scale={scale} size={18} color={readableText(d.fill)} />
                </div>
            ))}
            {items.map(({ table, fill, stroke, color, sub, disabled, title }) => (
                <button
                    key={table.id}
                    type="button"
                    disabled={disabled}
                    title={title}
                    onClick={() => onOpen(table)}
                    className={`absolute transition-transform ${disabled ? 'cursor-not-allowed opacity-60' : 'hover:scale-[1.03]'}`}
                    style={{ ...tableBoxStyle(table), zIndex: zOf.get(`t${table.id}`) }}
                >
                    <FloorShape table={table} fill={fill} stroke={stroke} />
                    <FloorLabel table={table} scale={scale} sub={sub} color={color} />
                </button>
            ))}
        </>
    );

    return (
        <div ref={outerRef} className="overflow-x-auto rounded-2xl border border-gray-200 bg-white">
            {area ? (
                <div
                    className="relative mx-auto overflow-hidden"
                    style={{ aspectRatio: `${area.w} / ${area.h}`, maxWidth: maxHeight ? (maxHeight * area.w) / area.h : undefined }}
                >
                    <div
                        ref={ref}
                        className="absolute"
                        style={{
                            width: `${(CANVAS_W / area.w) * 100}%`,
                            left: `${(-area.l / area.w) * 100}%`,
                            top: `${(-area.t / area.h) * 100}%`,
                            aspectRatio: `${CANVAS_W} / ${CANVAS_H}`,
                        }}
                    >
                        {content}
                    </div>
                </div>
            ) : (
                <div
                    ref={ref}
                    className="relative mx-auto min-w-[720px]"
                    style={{ aspectRatio: `${CANVAS_W} / ${CANVAS_H}`, maxWidth: maxHeight ? (maxHeight * CANVAS_W) / CANVAS_H : undefined }}
                >
                    {content}
                </div>
            )}
        </div>
    );
}
